import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import ScienceIcon from '@mui/icons-material/Science'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import { useTestDataStore } from '../../../stores/testDataStore'
import {
  generateTestData,
  clearTestData
} from '../../../services/testDataService'

const TestDataTab = () => {
  const showTestData = useTestDataStore(s => s.showTestData)
  const setShowTestData = useTestDataStore(s => s.setShowTestData)

  const [generating, setGenerating] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [confirmClearOpen, setConfirmClearOpen] = useState(false)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info'
  }>({ open: false, message: '', severity: 'success' })

  const showSnack = (message: string, severity: 'success' | 'error' | 'info') =>
    setSnackbar({ open: true, message, severity })

  // -----------------------------------------------------------------------
  // Generate (wipes + reloads the full test data set)
  // -----------------------------------------------------------------------
  const handleGenerate = async () => {
    setGenerating(true)
    try {
      await generateTestData()
      showSnack(
        'Test data generated successfully. Toggle "Show test data" to view it across all admin tabs.',
        'success'
      )
    } catch (err) {
      showSnack(
        `Failed to generate test data: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        'error'
      )
    } finally {
      setGenerating(false)
    }
  }

  // -----------------------------------------------------------------------
  // Clear all test data
  // -----------------------------------------------------------------------
  const handleClear = async () => {
    setConfirmClearOpen(false)
    setClearing(true)
    try {
      const count = await clearTestData()
      showSnack(
        `Cleared ${count} test document${
          count !== 1 ? 's' : ''
        } from Firestore.`,
        'success'
      )
      // Turn off the toggle so cleared data doesn't flash in admin tables
      if (showTestData) setShowTestData(false)
    } catch (err) {
      showSnack(
        `Failed to clear test data: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        'error'
      )
    } finally {
      setClearing(false)
    }
  }

  const busy = generating || clearing

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 1 }}>
        Test Data
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Populate Firestore with a fixed set of demo users, dives, join requests,
        transactions and ratings around the Newcastle&nbsp;NSW and Central
        Coast&nbsp;NSW area. All test records are tagged with a&nbsp;
        <code>testData:&nbsp;true</code> flag and are fully isolated from real
        data. Clicking <strong>Generate</strong> a second time wipes the
        existing set and reloads it fresh.
      </Typography>

      {/* Session toggle */}
      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Visibility (this browser session only)
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showTestData}
              onChange={e => setShowTestData(e.target.checked)}
              color='warning'
            />
          }
          label={
            showTestData
              ? 'Showing test data in all admin tabs'
              : 'Test data hidden from admin tabs and app'
          }
        />
        {showTestData && (
          <Alert severity='warning' sx={{ mt: 1.5 }}>
            Test data mode is <strong>ON</strong>. Admin tabs now display{' '}
            <strong>only</strong> test records. Real data is hidden until you
            turn this off.
          </Alert>
        )}
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* Actions */}
      <Box
        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <Button
          variant='contained'
          color='primary'
          size='large'
          startIcon={
            generating ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              <ScienceIcon />
            )
          }
          onClick={handleGenerate}
          disabled={busy}
        >
          {generating ? 'Generating…' : 'Generate Test Data'}
        </Button>

        <Button
          variant='outlined'
          color='error'
          size='large'
          startIcon={
            clearing ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              <DeleteSweepIcon />
            )
          }
          onClick={() => setConfirmClearOpen(true)}
          disabled={busy}
        >
          {clearing ? 'Clearing…' : 'Clear All Test Data'}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant='caption' color='text.secondary'>
          What gets generated:&nbsp; 10 dummy divers with verified PADI
          accreditations &nbsp;·&nbsp; 30 dives using existing dive sites
          &nbsp;·&nbsp; join requests &nbsp;·&nbsp; transactions &nbsp;·&nbsp;
          ratings
        </Typography>
      </Box>

      {/* Confirm clear dialog */}
      <Dialog
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Clear all test data?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This will permanently delete every document tagged as test data
            across all collections. Real user and dive data will not be
            affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button variant='contained' color='error' onClick={handleClear}>
            Yes, clear test data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
          sx={{ maxWidth: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default TestDataTab
